/* eslint-disable */
import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Button } from '@mui/material';
import CheckPicker from 'rsuite/CheckPicker';
import Input from 'rsuite/Input';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import styles from './VariantBatchSizePage.module.scss';
import { notificationServices } from '../../../Services';
import variantBatchSizeServices from '../../../Services/variantBatchSize.services';
import { BackdropLoader } from '../../../Components/Loaders';
import { useProductData, classes, useQuery } from '@/Helpers';
import { formActions } from '@/Actions';
import PoTableMultiple from '../../../Components/POTableMultiple/poTable';

// Allowed SKU types for batch size configuration
const ALLOWED_SKU_TYPES = ['PRODUCT', 'UNPACKED_PRODUCT', 'INTERMEDIATE'];

// Unit conversion mapping (lower unit -> higher unit display)
const UNIT_CONVERSION_MAP = {
	gram: { displayUnit: 'kg', multiplier: 1000 },
	gm: { displayUnit: 'kg', multiplier: 1000 },
	g: { displayUnit: 'kg', multiplier: 1000 },
	ml: { displayUnit: 'ltr', multiplier: 1000 },
	milliliter: { displayUnit: 'ltr', multiplier: 1000 },
	millilitre: { displayUnit: 'ltr', multiplier: 1000 },
	count: { displayUnit: 'pcs', multiplier: 1 },
};

// Helper function to get display unit (higher unit)
const getDisplayUnit = (unit) => {
	if (!unit) return unit;
	const lowerUnit = unit.toLowerCase();
	return UNIT_CONVERSION_MAP[lowerUnit]?.displayUnit || unit;
};

// Helper function to convert value from lower unit (BE) to higher unit (display)
const convertToDisplayValue = (value, unit) => {
	if (value === '' || value === null || value === undefined) return '';
	if (!unit) return value;
	const lowerUnit = unit.toLowerCase();
	const conversion = UNIT_CONVERSION_MAP[lowerUnit];
	if (conversion) {
		return Number(value) / conversion.multiplier;
	}
	return value;
};

// Helper function to convert value from higher unit (display) to lower unit (BE)
const convertToBackendValue = (value, unit) => {
	if (value === '' || value === null || value === undefined) return '';
	if (!unit) return Number(value);
	const lowerUnit = unit.toLowerCase();
	const conversion = UNIT_CONVERSION_MAP[lowerUnit];
	if (conversion) {
		return Number(value) * conversion.multiplier;
	}
	return Number(value);
};

const VariantBatchSizePage = (props) => {
	const { fetchStoresList, stores } = props;
	const listRef = useRef(null);
	const hasFetchedMapping = useRef(false);
	const query = useQuery();
	const productId = query.get('productId');
	const variantId = query.get('variantId');
	const storeId = query.get('storeId');
	const productName = query.get('productName');
	const [searchQuery, setSearchQuery] = useState('');
	const [batchSizeState, setBatchSizeState] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [selectedProductIds, setSelectedProductIds] = useState([]);
	const [selectedStoreIds, setSelectedStoreIds] = useState([]);
	const [headers, setHeaders] = useState([]);
	const [flatData, setFlatData] = useState({});
	const [childrenMapping, setChildrenMapping] = useState({});
	const [skuTypeFilter, setSkuTypeFilter] = useState('ALL');

	const products = useSelector(
		(state) => state?.form?.ALL_PRODUCTS?.data?.LIST
	);
	const dispatch = useDispatch();

	useEffect(() => {
		if (Array.isArray(products) && products?.length === 0) {
			useProductData(dispatch);
		}
	}, [products]);

	useEffect(() => {
		setIsLoading(true);
		fetchStoresList({
			entity: 'STORES',
			req: {
				params: {
					id: '',
				},
			},
			method: 'LIST',
		});
		setIsLoading(false);
	}, []);

	useEffect(() => {
		if (productName) {
			setSearchQuery(productName);
		}
	}, []);

	// Filter BASE type stores
	const BASE_STORES = useMemo(() => {
		return stores?.filter((store) => store?.type === 'BASE') || [];
	}, [stores]);

	// Get store data for CheckPicker
	const getStoreData = useMemo(() => {
		return BASE_STORES.map((store) => ({
			label: store?.name,
			value: store?.id,
		}));
	}, [BASE_STORES]);

	// Get selected stores based on selectedStoreIds
	const selectedStores = useMemo(() => {
		return BASE_STORES.filter((store) =>
			selectedStoreIds.includes(store?.id)
		);
	}, [BASE_STORES, selectedStoreIds]);

	// Generate headers from selected stores
	useEffect(() => {
		const storeHeaders = selectedStores.map((store) => ({
			colName: store?.name,
			id: store?.id,
			type: 'STORE',
			subCols: [],
		}));

		const headerData = [
			{
				colName: 'Item Name',
				subCols: [],
				flexGrow: 1,
				minWidth: 100,
			},
			{
				colName: 'SKU Code',
				subCols: [],
			},
			{
				colName: 'Unit',
				subCols: [],
			},
			{
				colName: 'Default',
				id: 0,
				type: 'ROOT',
				subCols: [],
			},
			...storeHeaders,
		];

		// Create flat data structure for selected stores
		const tempFlatData = {};
		const tempChildrenMapping = {};

		// Add ROOT entry for global default batch size (always available)
		tempFlatData['ROOT-0'] = {
			type: 'ROOT',
			id: 0,
			value: '',
			name: 'Default',
			access: true,
			parentValue: '',
			override: true,
		};

		selectedStores.forEach((store) => {
			tempFlatData[`STORE-${store?.id}`] = {
				type: 'STORE',
				id: store?.id,
				value: '',
				name: store?.name,
				access: true,
				parentValue: '',
				override: true,
			};
			// Build children mapping if store has children
			if (store?.children) {
				tempChildrenMapping[`STORE-${store?.id}`] = store.children.map(
					(child) => ({
						type: child?.type || 'STORE',
						id: child?.id,
					})
				);
			}
		});
		setFlatData(tempFlatData);
		setChildrenMapping(tempChildrenMapping);
		setHeaders(headerData);
	}, [selectedStores]);

		// Flatten products data - only include allowed SKU types
	const flattenedData = useMemo(() => {
		const result = [];
		products?.forEach((product) => {
			product?.variants?.forEach((variant) => {
				// Only include variants with allowed SKU types
				if (ALLOWED_SKU_TYPES.includes(product?.skuType)) {
					result.push({
						...product,
						...variant,
						productName: product?.productName,
						variantName:
							variant?.displayName || variant?.variantName,
						productNameDisplay: `${product?.productName} (${
							variant?.displayName || variant?.variantName
						})`,
						uniqueKey: `${product?.id}__${variant?.id}`,
						variantId: variant?.id,
					});
				}
			});
		});
		return result;
	}, [products]);

	// Fetch batch size mapping when stores and products are available
	useEffect(() => {
		if (
			BASE_STORES?.length > 0 &&
			flattenedData?.length > 0 &&
			!hasFetchedMapping.current
		) {
			hasFetchedMapping.current = true;
			fetchBatchSizeMapping();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [BASE_STORES?.length, flattenedData?.length]);

	const fetchBatchSizeMapping = async () => {
		setIsLoading(true);
		try {
			// Fetch all batch sizes in a single API call
			const response = await variantBatchSizeServices.getBatchSizeList();

			if (
				response?.statusCode === 200 ||
				response?.status === 'Success'
			) {
				const batchSizesData = response?.data?.batchSizes || {};
				const allMappings = {};
				const existingProductIds = new Set();
				const existingStoreIds = new Set();

				// Process the hierarchical data structure
				Object.entries(batchSizesData).forEach(
					([variantId, typeData]) => {
						const numericVariantId = Number(variantId);
						allMappings[numericVariantId] = {};

						// Find the product unit for this variant
						const productForVariant = flattenedData.find(
							(p) => p.variantId === numericVariantId
						);
						const productUnit = productForVariant?.unit;

						Object.entries(typeData).forEach(([key, data]) => {
							// key format: "<type>-<id>" e.g., "store-123", "region-1", "root-0"
							// Convert value from backend (lower unit) to display (higher unit)
							const displayValue = convertToDisplayValue(
								data?.value,
								productUnit
							);
							allMappings[numericVariantId][key.toUpperCase()] = {
								type: data?.type?.toUpperCase() || 'STORE',
								id: data?.id || 0,
								value: displayValue,
								access: data?.access,
							};

							// Extract store IDs from the key
							if (
								data?.type?.toUpperCase() === 'STORE' &&
								data?.id
							) {
								existingStoreIds.add(data?.id);
							}
						});

						existingProductIds.add(numericVariantId);
					}
				);
				if (productId && variantId) {
					existingProductIds.add(Number(variantId));
				}
				if (storeId) {
					existingStoreIds.add(Number(storeId));
				}
				setBatchSizeState(allMappings);
				setSelectedProductIds(Array.from(existingProductIds));
				setSelectedStoreIds(Array.from(existingStoreIds));
			} else {
				setBatchSizeState({});
				setSelectedProductIds([]);
				setSelectedStoreIds([]);
			}
		} catch (error) {
			console.error('Error fetching batch size mapping:', error);
			setBatchSizeState({});
			setSelectedProductIds([]);
			setSelectedStoreIds([]);
		} finally {
			setIsLoading(false);
		}
	};

	// Get product data for CheckPicker
	const getProductData = useMemo(() => {
		return flattenedData.map((item) => ({
			label: `${item?.productNameDisplay} (${item?.clientVariantId})`,
			value: item?.variantId,
		}));
	}, [flattenedData]);

	// Handle product selection from CheckPicker
	const handleProductSelection = (selectedIds) => {
		setSelectedProductIds(selectedIds);

		// Initialize batch size state for newly selected products
		const newBatchSizeState = { ...batchSizeState };
		selectedIds.forEach((variantId) => {
			if (!newBatchSizeState[variantId]) {
				newBatchSizeState[variantId] = { ...flatData };
			}
		});
		setBatchSizeState(newBatchSizeState);
	};

	// Get selected products data for the table
	const selectedProductsData = useMemo(() => {
		const selectedProducts = flattenedData.filter((item) =>
			selectedProductIds.includes(item?.variantId)
		);
		// Apply SKU type filter
		const skuFilteredProducts = selectedProducts.filter((item) => {
			if (skuTypeFilter === 'ALL') return true;
			if (skuTypeFilter === 'FG') return item?.skuType === 'PRODUCT';
			if (skuTypeFilter === 'SFG')
				return item?.skuType === 'INTERMEDIATE';
			return true;
		});

		const urlVariantId = Number(variantId);

		// Apply search filter
		return skuFilteredProducts.filter((item) => {
			// Always include the item if it matches the variantId in the URL
			if (urlVariantId && item.variantId === urlVariantId) return true;

			const normalizedSearch = searchQuery?.trim()?.toLowerCase();
			if (!normalizedSearch) return true;

			return (
				item?.productNameDisplay
					?.toLowerCase()
					?.includes(normalizedSearch) ||
				item?.clientVariantId?.toLowerCase()?.includes(normalizedSearch)
			);
		});
	}, [
		flattenedData,
		searchQuery,
		selectedProductIds,
		skuTypeFilter,
		variantId,
	]);

	// Update value function similar to CreateVendorContractPage
	const updateValue = (
		variantIdToPass,
		typeToPass,
		idToPass,
		inputValueToPass,
		modifiedToPass,
		goToDepthToPass = true
	) => {
		const updateBatchSizeValue = (
			variantId,
			type,
			id,
			inputValue,
			modified,
			updatedBatchSizes = {},
			goToDepth = false
		) => {
			if (updatedBatchSizes[variantId]) {
				const key = `${type}-${id}`;
				const updatedProduct = {
					...updatedBatchSizes[variantId],
				};

				if (updatedProduct[key]) {
					updatedProduct[key] = {
						...updatedProduct[key],
						[modified]: inputValue,
					};
				} else {
					// Key doesn't exist in this variant's data, create it
					updatedProduct[key] = {
						type,
						id,
						value: '',
						access: true,
						parentValue: '',
						override: true,
						[modified]: inputValue,
					};
				}
				// eslint-disable-next-line no-param-reassign
				updatedBatchSizes[variantId] = updatedProduct;

				const batchItem = updatedBatchSizes?.[variantId]?.[key];
				childrenMapping[key]?.forEach((child) => {
					const valueToPass = inputValue
						? batchItem?.value
						: batchItem?.parentValue;
					if (
						modified === 'value' ||
						(modified === 'parentValue' && !batchItem?.override)
					) {
						updateBatchSizeValue(
							variantId,
							child?.type,
							child?.id,
							inputValue,
							'parentValue',
							updatedBatchSizes,
							goToDepth
						);
					} else if (
						modified === 'access' ||
						modified === 'override'
					) {
						updateBatchSizeValue(
							variantId,
							child?.type,
							child?.id,
							valueToPass,
							'parentValue',
							updatedBatchSizes,
							goToDepth
						);
					} else if (
						goToDepth &&
						modified === 'parentValue' &&
						!batchItem?.override
					) {
						updateBatchSizeValue(
							variantId,
							child?.type,
							child?.id,
							batchItem?.value,
							'parentValue',
							updatedBatchSizes,
							goToDepth
						);
					}
				});
			} else {
				const key = `${type}-${id}`;
				// eslint-disable-next-line no-param-reassign
				updatedBatchSizes[variantId] = { ...flatData };

				const updatedProduct = {
					...updatedBatchSizes[variantId],
				};

				updatedProduct[key] = {
					...updatedProduct[key],
					type,
					id,
					[modified]: inputValue,
				};
				// eslint-disable-next-line no-param-reassign
				updatedBatchSizes[variantId] = updatedProduct;
			}
		};

		const updatedBatchSizes = {
			...batchSizeState,
		};

		updateBatchSizeValue(
			variantIdToPass,
			typeToPass,
			idToPass,
			inputValueToPass,
			modifiedToPass,
			updatedBatchSizes,
			goToDepthToPass
		);

		setBatchSizeState(updatedBatchSizes);
	};

	// Transform headers for PoTableMultiple format
	const tableData = useMemo(() => {
		if (!headers?.length || !selectedProductsData?.length) {
			return { headers: [], rows: [] };
		}

		// Transform headers to PoTableMultiple format
		const transformedHeaders = headers.map((header) => {
			let unit = header?.colName === 'Unit';
			return {
				name: header?.colName,
				id: header?.id ?? header?.colName,
				type: header?.type,
				isUnit: unit,
				flexGrow: header?.flexGrow,
				minWidth: header?.minWidth,
			};
		});

		// Transform rows data for PoTableMultiple
		const transformedRows = selectedProductsData.map((item) => {
			const originalUnit = item?.unit;
			// Convert unit to higher unit for display (gram -> kg, ml -> ltr, count -> count)
			const displayUnit = getDisplayUnit(originalUnit);
			return {
				...item,
				'Item Name': `${item?.productName} (${item?.variantName})`,
				'SKU Code': item?.clientVariantId || '-',
				Unit: displayUnit,
				originalUnit: originalUnit, // Keep original unit for backend conversion
			};
		});

		return {
			headers: transformedHeaders,
			rows: transformedRows,
		};
	}, [headers, selectedProductsData]);

	// Render cell data for PoTableMultiple
	const renderCellData = (rowData, rowIndex, header) => {
		// Handle Item Name column
		if (header?.name === 'Item Name') {
			return (
				<div
					className={`w-full flex-1 text-left text-[12px] flex gap-2 items-center p-1 relative`}
				>
					{rowData?.['Item Name']}
				</div>
			);
		}

		// Handle SKU Code column
		if (header?.name === 'SKU Code') {
			return (
				<div className='w-full text-center text-[12px] p-1 font-medium text-gray-600'>
					{rowData?.['SKU Code']}
				</div>
			);
		}

		// Handle Unit column
		if (header?.name === 'Unit') {
			return (
				<div style={{ fontWeight: 600, textAlign: 'center' }}>
					{rowData?.Unit}
				</div>
			);
		}

		const currentVariantId = rowData?.variantId;
		const type = header?.type;
		const id = header?.id;
		const flatItemData =
			batchSizeState?.[currentVariantId]?.[`${type}-${id}`];
		const isAccessEnabled = flatItemData?.access !== false;
		const isHighlighted =
			String(currentVariantId) === String(variantId) &&
			String(id) === String(storeId);

		return (
			<div
				className={classes(
					styles['variant-input'],
					styles['editable-input']
				)}
			>
				{isAccessEnabled ? (
					<input
						className={classes(
							styles.batch_input,
							isHighlighted && styles['highlight-input']
						)}
						type='number'
						min={0}
						value={flatItemData?.value ?? ''}
						onChange={(e) => {
							const inputValue = e?.target?.value;
							const isValidNumber = /^[0-9]*\.?[0-9]*$/.test(
								inputValue
							);
							if (inputValue === '' || isValidNumber) {
								updateValue(
									currentVariantId,
									type,
									id,
									inputValue,
									'value'
								);
							}
						}}
						placeholder='Batch Size'
					/>
				) : (
					<span className={styles.batch_input}>N.A.</span>
				)}
			</div>
		);
	};

	const onSubmit = async () => {
		if (BASE_STORES?.length === 0) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'No BASE stores available',
			});
			return;
		}

		if (selectedProductIds.length === 0) {
			notificationServices.generateNotification({
				type: 'warning',
				message: 'Please select at least one product',
			});
			return;
		}

		setIsLoading(true);
		try {
			// Format batchSizes in the required payload format
			const batchSizes = {};

			// Create a set of valid store keys based on selectedStoreIds
			const validStoreKeys = new Set(
				selectedStoreIds.map((storeId) => `STORE-${storeId}`)
			);
			// Always include ROOT-0 for default batch size
			validStoreKeys.add('ROOT-0');

			// Only process variants that are currently selected
			Object.entries(batchSizeState).forEach(([variantId, storeData]) => {
				// Filter out variants not in selectedProductIds
				if (!selectedProductIds.includes(Number(variantId))) {
					return;
				}

				if (!batchSizes[variantId]) {
					batchSizes[variantId] = {};
				}

				// Find the product unit for this variant to convert to backend format
				const productForVariant = flattenedData.find(
					(p) => p.variantId === Number(variantId)
				);
				const productUnit = productForVariant?.unit;

				Object.entries(storeData).forEach(([key, data]) => {
					// Filter out stores not in selectedStoreIds (except ROOT-0)
					if (!validStoreKeys.has(key)) {
						return;
					}

					const displayValue = data?.value;
					// Only include entries with valid batch size values
					if (
						displayValue !== undefined &&
						displayValue !== '' &&
						Number(displayValue) >= 0
					) {
						// Convert from display unit (higher) to backend unit (lower)
						const backendValue = convertToBackendValue(
							displayValue,
							productUnit
						);
						batchSizes[variantId][key] = {
							id: data?.id || 0,
							value: backendValue,
							access: 1,
							type: data?.type?.toLowerCase() || 'store',
						};
					}
				});
			});

			// Remove empty variant entries
			Object.keys(batchSizes).forEach((variantId) => {
				if (Object.keys(batchSizes[variantId]).length === 0) {
					delete batchSizes[variantId];
				}
			});

			if (Object.keys(batchSizes).length === 0) {
				notificationServices.generateNotification({
					type: 'warning',
					message: 'No batch sizes to save',
				});
				setIsLoading(false);
				return;
			}

			const response =
				await variantBatchSizeServices.createBatchSizeMapping({
					data: {
						batchSizes,
					},
				});

			if (
				response?.statusCode === 200 ||
				response?.status === 'Success'
			) {
				notificationServices.generateNotification({
					type: 'success',
					message: 'Batch size mapping saved successfully',
				});
				// Refresh the data
				fetchBatchSizeMapping();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: response?.message || 'Failed to save batch sizes',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: 'Some error occurred while saving',
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className={styles.root}>
			<BackdropLoader showProgress isLoading={isLoading} />
			<span className='font-semibold text-base whitespace-nowrap'>
				Variant Batch Size
			</span>
			<div className='flex flex-wrap items-end gap-4 p-4 bg-white rounded-lg shadow-sm border border-gray-100 mb-4'>
				<div className='flex flex-wrap gap-4 flex-1'>
					<div className='flex flex-col gap-1.5 flex-1 min-w-[200px] max-w-[320px] items-start'>
						<label className='text-[13px] font-semibold text-gray-700 leading-[1.2] text-left w-full m-0 p-0'>
							Selected Kitchens{' '}
							<span className='text-gray-400 font-normal'>
								({selectedStoreIds?.length})
							</span>
						</label>
						<CheckPicker
							data={getStoreData}
							placeholder='Select Stores'
							value={selectedStoreIds}
							onChange={setSelectedStoreIds}
							virtualized
							menuStyle={{
								zIndex: 99999,
							}}
							cleanable={false}
							searchable
							className='w-full [&_.rs-picker-toggle]:border [&_.rs-picker-toggle]:border-gray-300 [&_.rs-picker-toggle]:rounded [&_.rs-picker-toggle]:px-2.5 [&_.rs-picker-toggle]:py-1.5 [&_.rs-picker-toggle]:text-[13px] [&_.rs-picker-toggle]:w-full [&_.rs-picker-toggle]:h-9 [&_.rs-picker-toggle]:leading-[1.2] [&_.rs-picker-toggle]:text-left [&_.rs-picker-toggle]:flex [&_.rs-picker-toggle]:items-center [&_.rs-picker-toggle]:justify-start [&_.rs-picker-toggle:hover]:border-blue-500 [&_.rs-picker-toggle:focus]:border-blue-500 [&_.rs-picker-toggle:focus]:shadow-[0_0_0_2px_rgba(59,130,246,0.1)]'
						/>
					</div>
					<div className='flex flex-col gap-1.5 flex-1 min-w-[200px] max-w-[400px] items-start'>
						<label className='text-[13px] font-semibold text-gray-700 leading-[1.2] text-left w-full m-0 p-0'>
							Selected Products{' '}
							<span className='text-gray-400 font-normal'>
								({selectedProductIds?.length})
							</span>
						</label>
						<CheckPicker
							data={getProductData}
							placeholder='Select Products to Define Batch Size'
							value={selectedProductIds}
							onChange={handleProductSelection}
							virtualized
							menuStyle={{
								zIndex: 99999,
							}}
							cleanable={false}
							searchable
							className='w-full [&_.rs-picker-toggle]:border [&_.rs-picker-toggle]:border-gray-300 [&_.rs-picker-toggle]:rounded [&_.rs-picker-toggle]:px-2.5 [&_.rs-picker-toggle]:py-1.5 [&_.rs-picker-toggle]:text-[13px] [&_.rs-picker-toggle]:w-full [&_.rs-picker-toggle]:h-9 [&_.rs-picker-toggle]:leading-[1.2] [&_.rs-picker-toggle]:text-left [&_.rs-picker-toggle]:flex [&_.rs-picker-toggle]:items-center [&_.rs-picker-toggle]:justify-start [&_.rs-picker-toggle:hover]:border-blue-500 [&_.rs-picker-toggle:focus]:border-blue-500 [&_.rs-picker-toggle:focus]:shadow-[0_0_0_2px_rgba(59,130,246,0.1)]'
						/>
					</div>
					<div className='flex flex-col gap-1.5 min-w-[200px] max-w-[250px] items-start'>
						<label className='text-[13px] font-semibold text-gray-700 leading-[1.2] text-left w-full m-0 p-0'>
							Search Products
						</label>
						<input
							type='text'
							value={searchQuery}
							onChange={(e) => {
								setSearchQuery(e.target.value);
							}}
							placeholder='Search in table...'
							className='w-full border border-gray-300 rounded px-2.5 py-1.5 text-[13px] h-9 leading-[1.2] bg-white text-gray-700 transition-all hover:border-blue-500 focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_2px_rgba(59,130,246,0.1)] placeholder:text-gray-400'
						/>
					</div>
				</div>
				<div className='flex flex-row gap-3 items-end'>
					{BASE_STORES?.length === 0 && (
						<p className='text-[13px] text-red-500 font-medium'>
							No BASE stores found
						</p>
					)}
					<div className='flex flex-col gap-1.5 items-start'>
						<label className='text-[13px] font-semibold text-gray-700 leading-[1.2] text-left w-full m-0 p-0'>
							SKU Type
						</label>
						<div className='flex gap-0'>
							{['ALL', 'FG', 'SFG'].map((type, index, arr) => (
								<button
									key={type}
									type='button'
									onClick={() => setSkuTypeFilter(type)}
									className={`px-4 py-1.5 text-[13px] font-medium border h-9 leading-[1.2] text-center flex items-center justify-center uppercase transition-all ${
										skuTypeFilter === type
											? 'bg-amber-300 text-black border-amber-300 font-semibold'
											: 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300'
									} ${index === 0 ? 'rounded-l' : ''} ${
										index === arr.length - 1
											? 'rounded-r'
											: ''
									}`}
								>
									{type}
								</button>
							))}
						</div>
					</div>
					{selectedProductIds?.length > 0 && (
						<button
							onClick={onSubmit}
							className='px-5 py-2 text-[13px] font-semibold bg-amber-400 text-black border border-amber-400 rounded h-9 leading-[1.2] text-center flex items-center justify-center transition-all hover:bg-amber-500 hover:border-amber-500 focus:outline-none focus:shadow-[0_0_0_2px_rgba(251,191,36,0.3)] active:bg-amber-600'
						>
							Save
						</button>
					)}
				</div>
			</div>
			<div ref={listRef} className={styles['list-container']}>
				{tableData?.headers?.length > 0 &&
					tableData?.rows?.length > 0 && (
						<PoTableMultiple
							tableData={tableData}
							renderCellData={renderCellData}
							fixedColumns={['Item Name', 'SKU Code', 'Unit']}
							columnWidth={200}
						/>
					)}
				{selectedProductsData?.length === 0 &&
					selectedProductIds?.length === 0 && (
						<div className={styles['empty-state']}>
							<p>Select products to configure batch sizes</p>
						</div>
					)}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const data = state?.form?.STORES?.data?.LIST;
	return {
		stores: data,
	};
};

const mapDispatchToProps = {
	fetchStoresList: formActions.gateway,
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(VariantBatchSizePage);
